import React, { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CommonButton from "../../../components/CommonButton";
import BundleCard from "../../../components/Admin/BundleCard";
import { 
    useLazyGetAllBundleQuery, 
    useUpdateBundleStatusMutation, 
    useLazyDeleteBundleQuery 
} from "../../../redux/actions/Admin/ProductAdmin";

const AdminBundleScreen: React.FC = () => {
    const navigate = useNavigate();

    // API Traversal hooks
    const [triggerGetAllBundles, { data: bundlesData, isLoading }] = useLazyGetAllBundleQuery();
    const [updateBundleStatus] = useUpdateBundleStatusMutation();
    const [deleteBundle] = useLazyDeleteBundleQuery();

    useEffect(() => {
        triggerGetAllBundles();
    }, [triggerGetAllBundles]);

    const handleToggleStatus = async (id: number, currentStatus: boolean) => {
        try {
            await updateBundleStatus({ id, data: { isActive: !currentStatus } }).unwrap();
            triggerGetAllBundles();
        } catch (err) {
            console.error("Failed to change status:", err);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Are you sure you want to delete this bundle?")) {
            try {
                await deleteBundle(id).unwrap();
                triggerGetAllBundles();
            } catch (err) {
                console.error("Failed to delete bundle:", err);
            }
        }
    };

    return (
        <div className="p-5 text-left">
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between items-center gap-4 border-b border-slate-100 pb-5">
                <div>
                    <h1 className="text-2xl font-bold text-logoHeading">
                        Bundle Management
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Create, update, and manage product bundles with custom pricing.
                    </p>
                </div>
                <CommonButton
                    text="Create Bundle"
                    variant="filled"
                    rounded="md"
                    icon={<Plus size={18} />}
                    onClick={() => navigate("createbundlescreen")}
                />
            </div>

            <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-800">
                    All Bundles
                </h2>
            </div>

            {isLoading ? (
                <div className="py-20 flex flex-col items-center justify-center gap-2 text-sm text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
                    <span>Loading bundles...</span>
                </div>
            ) : bundlesData?.data && bundlesData.data.length > 0 ? (
                <div className="flex flex-col gap-4 w-full">
                    {bundlesData.data.map((bundle, index) => (
                        <BundleCard
                            key={bundle.bundleId}
                            bundle={{
                                id: bundle.bundleId,
                                name: bundle.name,
                                bundlePrice: bundle.price.toFixed(2),
                                isActive: bundle.isActive,
                                totalProducts: bundle.totalProducts
                            }}
                            index={index}
                            onEdit={() => navigate(`createbundlescreen`, { state: { bundleId: bundle.bundleId } })}
                            // onDelete={() => handleDelete(bundle.bundleId)}
                            onToggleStatus={() => handleToggleStatus(bundle.bundleId, bundle.isActive)}
                        />
                    ))}
                </div>
            ) : (
                <div className="border border-dashed border-slate-200 rounded-2xl p-16 text-center text-sm text-gray-400">
                    No active bundles found. Click "Create Bundle" to add your first one.
                </div>
            )}
        </div>
    );
};

export default AdminBundleScreen;